import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { ArrowRight, Target, Pencil, Skull } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money, usePrivacy } from "../context/PrivacyContext";
import { fmtEUR, fmtDate } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import CopilotoWidget from "../components/CopilotoWidget";

const PIE_COLORS = ["#0A0A0A", "#5E5E62", "#8F8F94", "#B8B8BD", "#D4D4D8", "#10B981", "#3B82F6", "#F59E0B"];

function Kpi({ label, value, testId, accent }) {
  return (
    <div className="border border-border rounded-lg p-5 bg-background hover:border-foreground/30 transition-colors">
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">{label}</div>
      <Money value={value} testId={testId} className={`block mt-2 text-2xl sm:text-3xl font-semibold tabular tracking-tight ${accent || ""}`} />
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-8 w-64 bg-secondary rounded" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => <div key={i} className="h-28 bg-secondary rounded-lg" />)}
      </div>
      <div className="h-64 bg-secondary rounded-lg" />
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [nw, setNw] = useState(null);
  const [zombie, setZombie] = useState(null);
  const [view, setView] = useState("real"); // 'real' | 'compromisos'
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalValue, setGoalValue] = useState("");
  const { hidden } = usePrivacy();

  const load = () => Promise.all([
    api.get("/dashboard").then((r) => setData(r.data)),
    api.get("/networth").then((r) => setNw(r.data)).catch(() => {}),
    api.get("/zombie/summary").then((r) => setZombie(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const saveGoal = async () => {
    try {
      await api.put("/family", { goal_amount: parseFloat(String(goalValue).replace(",", ".")) || 0 });
      toast.success("Objetivo actualizado");
      setGoalOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <Skeleton />;

  const savingsPositive = data.capacidad_ahorro >= 0;
  const progreso = data.progreso_pct != null ? Math.min(100, Math.max(0, data.progreso_pct)) : null;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter">Tu radar financiero</h1>
        <p className="text-sm text-muted-foreground mt-1">{data.family_name ? `${data.family_name} · situación actual y hacia dónde vais.` : "Situación actual y hacia dónde vas."}</p>
      </div>

      <CopilotoWidget />

      {/* Widget Gastos Zombie 🧟 */}
      {zombie && zombie.count_active > 0 && (
        <Link to="/gastos-zombie" data-testid="zombie-widget" className="block border border-border rounded-lg p-5 hover:border-foreground/30 transition-colors">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center"><Skull size={20} /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Gastos Zombie · Bajo vigilancia</div>
                <div className="text-sm text-muted-foreground mt-0.5">{zombie.count_active} incidencia{zombie.count_active === 1 ? "" : "s"} activa{zombie.count_active === 1 ? "" : "s"}{zombie.last_detected ? ` · última ${new Date(zombie.last_detected).toLocaleDateString("es-ES")}` : ""}</div>
              </div>
            </div>
            <div className="text-right">
              <Money value={zombie.total_watched} className="block text-2xl font-semibold tabular" />
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-1">Total vigilado</div>
            </div>
          </div>
          {zombie.top && zombie.top.length > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {zombie.top.slice(0, 4).map((z) => (
                <div key={z.id} className="flex items-center justify-between text-sm border-t border-border pt-2">
                  <span className="truncate">{z.provider_name || z.title}</span>
                  <Money value={z.amount || 0} className="font-semibold tabular" />
                </div>
              ))}
            </div>
          )}
        </Link>
      )}

      {/* Libertad financiera */}
      <div className="rounded-lg bg-primary text-primary-foreground p-6" data-testid="freedom-banner">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-[220px]">
            <div className="flex gap-1 mb-1 rounded-md bg-white/10 p-0.5 w-fit" data-testid="patrimonio-view-toggle">
              <button onClick={() => setView("real")} data-testid="view-toggle-real" className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded ${view === "real" ? "bg-white text-primary" : "text-white/70 hover:text-white"}`}>Patrimonio real</button>
              <button onClick={() => setView("compromisos")} data-testid="view-toggle-compromisos" className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold rounded ${view === "compromisos" ? "bg-white text-primary" : "text-white/70 hover:text-white"}`}>Compromisos futuros</button>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">
              {view === "real" ? "¿Cuál es mi patrimonio real hoy?" : "¿Cuánto me queda por pagar si mantengo todos los contratos?"}
            </div>
            <Money
              value={view === "real" ? (nw?.neto_real ?? data.patrimonio_neto) : ((nw?.bruto ?? 0) - (nw?.compromisos_futuros ?? nw?.deuda ?? 0))}
              testId="freedom-neto"
              className="block mt-1 text-3xl sm:text-4xl font-semibold tabular"
            />
            <div className="text-[10px] text-white/40 mt-1">
              {view === "real" ? "Bruto − Deuda cancelable hoy" : "Bruto − Pending amount total"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">Previsto en 12 meses</div>
            <Money value={data.neto_previsto_12m} testId="freedom-previsto" className={`block mt-1 text-2xl sm:text-3xl font-semibold tabular ${data.neto_previsto_12m >= data.patrimonio_neto ? "text-emerald-400" : "text-red-400"}`} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50 flex items-center gap-1"><Target size={11} /> Objetivo libertad financiera</div>
            {data.objetivo > 0 ? (
              <button data-testid="edit-goal-button" onClick={() => { setGoalValue(data.objetivo); setGoalOpen(true); }} className="flex items-center gap-2 mt-1 group">
                <Money value={data.objetivo} testId="freedom-objetivo" className="text-2xl sm:text-3xl font-semibold tabular" />
                <Pencil size={13} className="text-white/40 group-hover:text-white" />
              </button>
            ) : (
              <Button data-testid="set-goal-button" onClick={() => { setGoalValue(""); setGoalOpen(true); }} variant="secondary" size="sm" className="mt-1">Definir objetivo</Button>
            )}
          </div>
        </div>
        {progreso != null && (
          <div className="mt-5">
            <div className="flex justify-between text-xs text-white/60 mb-1.5">
              <span>Progreso hacia tu libertad financiera</span>
              <span data-testid="freedom-progreso" className="font-bold text-white">{data.progreso_pct} %</span>
            </div>
            <div className="h-2 bg-white/15 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-400 rounded-full transition-all duration-700" style={{ width: `${progreso}%` }} />
            </div>
          </div>
        )}
      </div>

      <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">Objetivo de libertad financiera</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">¿Qué patrimonio neto quiere alcanzar tu familia?</p>
          <Input data-testid="goal-input" inputMode="decimal" placeholder="Ej. 250000" value={goalValue} onChange={(e) => setGoalValue(e.target.value)} />
          <Button data-testid="goal-submit-button" onClick={saveGoal} className="w-full">Guardar objetivo</Button>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi label="Patrimonio bruto" value={data.patrimonio_bruto} testId="kpi-patrimonio-bruto" />
        <Kpi label="Deuda pendiente" value={data.deuda_total} testId="kpi-deuda-total" accent="text-red-600" />
        <Kpi label="Patrimonio neto" value={data.patrimonio_neto} testId="kpi-patrimonio-neto" />
        <Kpi label="Ingresos mensuales" value={data.ingresos_mensuales} testId="kpi-ingresos" accent="text-emerald-600" />
        <Kpi label="Gastos mensuales" value={data.gastos_mensuales} testId="kpi-gastos" />
        <Kpi label="Capacidad de ahorro" value={data.capacidad_ahorro} testId="kpi-ahorro" accent={savingsPositive ? "text-emerald-600" : "text-red-600"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pagos pendientes */}
        <div className="border border-border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">Pagos pendientes</h3>
            <Link to="/pagos" data-testid="link-pagos" className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1">Ver todo <ArrowRight size={12} /></Link>
          </div>
          <div className="space-y-2" data-testid="pagos-buckets">
            {[["Hoy", data.pagos.hoy], ["Próximos 7 días", data.pagos["7"]], ["Próximos 15 días", data.pagos["15"]], ["Próximos 30 días", data.pagos["30"]], ["Próximo trimestre", data.pagos.trimestre]].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                <span className="text-sm text-muted-foreground">{l}</span>
                <Money value={v} className="text-sm font-semibold tabular" />
              </div>
            ))}
          </div>
        </div>

        {/* Saldo proyectado */}
        <div className="border border-border rounded-lg p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-heading font-bold text-lg">Saldo proyectado</h3>
            <Link to="/proyecciones" data-testid="link-proyecciones" className="text-xs font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1">Escenarios <ArrowRight size={12} /></Link>
          </div>
          <div className="flex gap-6 mb-3 flex-wrap">
            {[["30 días", data.proyeccion["30"]], ["90 días", data.proyeccion["90"]], ["1 año", data.proyeccion["365"]]].map(([l, v]) => (
              <div key={l}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">{l}</div>
                <Money value={v} className={`text-lg font-semibold tabular ${v < 0 ? "text-red-600" : ""}`} />
              </div>
            ))}
          </div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.proyeccion_serie} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#8F8F94" }} tickLine={false} axisLine={false} interval={2} />
                <YAxis hide />
                <Tooltip formatter={(v) => (hidden ? "•••••• €" : fmtEUR(v))} contentStyle={{ borderRadius: 8, border: "1px solid #E5E5EA", fontSize: 12 }} />
                <Line type="monotone" dataKey="saldo" stroke="#0A0A0A" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Próximos pagos */}
        <div className="border border-border rounded-lg p-5">
          <h3 className="font-heading font-bold text-lg mb-4">Próximos vencimientos</h3>
          {data.proximos_pagos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin pagos próximos. Añade gastos recurrentes o financiaciones.</p>
          ) : (
            <div className="space-y-2" data-testid="proximos-pagos-list">
              {data.proximos_pagos.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                  <div>
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(p.date)}</div>
                  </div>
                  <Money value={p.amount} className="text-sm font-semibold tabular" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Distribución activos */}
        <div className="border border-border rounded-lg p-5">
          <h3 className="font-heading font-bold text-lg mb-4">Distribución de activos</h3>
          {data.activos_por_tipo.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin activos registrados. <Link to="/activos" className="underline">Añade el primero</Link>.</p>
          ) : (
            <div className="flex items-center gap-4">
              <div className="h-40 w-40 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.activos_por_tipo} dataKey="total" nameKey="tipo" innerRadius={45} outerRadius={70} paddingAngle={2}>
                      {data.activos_por_tipo.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => (hidden ? "•••••• €" : fmtEUR(v))} contentStyle={{ borderRadius: 8, border: "1px solid #E5E5EA", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 min-w-0">
                {data.activos_por_tipo.map((a, i) => (
                  <div key={a.tipo} className="flex items-center gap-2 text-sm">
                    <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-muted-foreground truncate">{a.tipo}</span>
                    <Money value={a.total} className="font-medium tabular ml-auto" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
